import { apiClient } from "./client";

/**
 * Ritiri valori — import degli export AZNext (docs/specs/ritiri-valori.md,
 * §§ 1.4 e 5). Allineato a `backend/src/routes/ritiri.ts`.
 *
 * Le date AZNext (`data_*_max`) sono ora locale italiana "da orologio": il
 * backend le salva senza fuso e le restituisce con i componenti in UTC. Vanno
 * mostrate così come sono, senza conversioni: vedi `formattaDataAznext`.
 */

export type FileImportRitiri = "anagrafica" | "versamenti" | "prelievi";

export interface ScartoImport {
  riga: number;
  motivo: string;
}

export interface AvvisoImport {
  riga: number;
  messaggio: string;
}

export interface RiepilogoFileImport {
  righe_lette: number;
  nuove: number;
  aggiornate: number;
  invariate: number;
  scartate: number;
  motivi_scarto: Record<string, number>;
  dettaglio_scarti: ScartoImport[];
  avvisi_totali: number;
  avvisi: AvvisoImport[];
}

export interface ReportImportRitiri {
  file: Partial<Record<FileImportRitiri, RiepilogoFileImport>>;
  doppioni_anagrafica: { id_macchina: string; righe: number[]; riga_scelta: number }[];
  righe_minime: string[];
  casseforti_non_collegate: { totale: number; id_macchina: string[] };
  /** Ora italiana, `YYYY-MM-DD HH:MM:SS`. */
  data_operazione_max: string | null;
  data_ricezione_max: string | null;
  /** Data di generazione dell'export indicata da chi carica (v1.18); assente nei report di prima. */
  generato_il?: string | null;
  /** Operazioni già salvate che l'export non aggiorna perché più vecchio (v1.18). */
  operazioni_piu_vecchie?: number;
  /** Avvisi sull'export che non bloccano (v1.18). */
  avvisi_export?: string[];
}

export interface EsitoImportRitiri {
  anteprima: boolean;
  import_id?: string;
  report: ReportImportRitiri;
}

export interface ImportRitiriStorico {
  id: string;
  origine: "manuale" | "collector";
  created_at: string;
  data_operazione_max: string | null;
  data_ricezione_max: string | null;
  /** Data di generazione dell'export (v1.18), ora italiana; null per gli import di prima e per la sola anagrafica. */
  generato_il: string | null;
  report: ReportImportRitiri;
  user: { id: string; full_name: string | null; email: string } | null;
}

export type FileSelezionati = Partial<Record<FileImportRitiri, File>>;

/**
 * `generatoIl`: data e ora di generazione dell'export, ora italiana, come
 * `YYYY-MM-DDTHH:MM` di un campo datetime-local (v1.18). Vuota non si invia.
 */
function formData(file: FileSelezionati, generatoIl: string): FormData {
  const fd = new FormData();
  // Prima il campo di testo, poi i file.
  if (generatoIl) fd.append("generato_il", generatoIl);
  for (const [campo, f] of Object.entries(file)) {
    if (f) fd.append(campo, f);
  }
  return fd;
}

export async function anteprimaImport(file: FileSelezionati, generatoIl = ""): Promise<EsitoImportRitiri> {
  const { data } = await apiClient.post<EsitoImportRitiri>("/ritiri/import/preview", formData(file, generatoIl), {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function confermaImport(file: FileSelezionati, generatoIl = ""): Promise<EsitoImportRitiri> {
  const { data } = await apiClient.post<EsitoImportRitiri>("/ritiri/import/commit", formData(file, generatoIl), {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export interface StoricoImportRitiri {
  /** Ultima `data_ricezione_portale` importata (§ 1.4), ora italiana. */
  dati_aggiornati_al: string | null;
  storico: ImportRitiriStorico[];
}

export async function storicoImport(): Promise<StoricoImportRitiri> {
  const { data } = await apiClient.get<StoricoImportRitiri>("/ritiri/import");
  return data;
}

/**
 * Data AZNext come `gg/mm/aaaa hh:mm`, letta dai componenti della stringa e
 * senza passare da `Date`: una conversione di fuso la sposterebbe di una o due
 * ore. Accetta sia `YYYY-MM-DD HH:MM:SS` sia l'ISO con `Z` del backend.
 */
export function formattaDataAznext(valore: string | null): string {
  if (!valore) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(valore);
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : valore;
}

/** Millisecondi di una data AZNext letta come se fosse UTC (i componenti sono l'ora italiana). */
export function msDataAznext(valore: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/.exec(valore);
  if (!m) return null;
  return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] ?? 0));
}

const FORMATO_ROMA = new Intl.DateTimeFormat("en-GB", {
  timeZone: "Europe/Rome",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

/**
 * Un istante reale (ms dal 1970, come `Date.now()`) → la sua ora italiana da
 * orologio, con la convenzione di `msDataAznext`. Tiene i millisecondi: i fusi
 * spostano l'ora di secondi interi.
 */
function orologioRoma(istante: number): number {
  const d = new Date(istante);
  const parti = Object.fromEntries(FORMATO_ROMA.formatToParts(d).map((p) => [p.type, p.value]));
  return Date.UTC(+parti.year, +parti.month - 1, +parti.day, +parti.hour, +parti.minute, +parti.second, d.getUTCMilliseconds());
}

/** Adesso in ora italiana da orologio, con la stessa convenzione di `msDataAznext`. */
export function msAdessoItalia(): number {
  return orologioRoma(Date.now());
}

/**
 * Data AZNext (ora italiana da orologio) → istante reale, confrontabile con
 * `Date.now()`. Si usa per le DURATE: due orari da orologio non si sottraggono,
 * perché tra i due può cadere il cambio dell'ora legale e la differenza
 * sbaglierebbe di un'ora. Lo scostamento è quello di Europe/Rome valido a
 * quella data: una prima stima e una correzione con lo scostamento
 * dell'istante stimato, che vicino al cambio può essere diverso. L'ora che al
 * cambio si ripete o non esiste resta ambigua, come nel resto del modulo.
 * Per MOSTRARE le date si continua a usare `formattaDataAznext`.
 */
export function istanteDataAznext(valore: string): number | null {
  const orologio = msDataAznext(valore);
  if (orologio === null) return null;
  const stima = orologio - (orologioRoma(orologio) - orologio);
  return orologio - (orologioRoma(stima) - stima);
}

/** "Dati aggiornati al …" più vecchi di `ore` ore (§ 1.4: avviso oltre 24 h), in tempo reale trascorso. */
export function datiVecchi(valore: string | null, ore = 24): boolean {
  if (!valore) return false;
  const istante = istanteDataAznext(valore);
  return istante !== null && Date.now() - istante > ore * 3600 * 1000;
}

// ----------------------------------------------------------------------------
// Motore di calcolo: API di lettura (§ 5). Il frontend mostra questi risultati
// e non li ricalcola (§ 0.6). Le ore (`E1`, `adesso`, `ultimo_*`) sono ora
// italiana `YYYY-MM-DD HH:MM:SS`, i giorni (`D`, `P1`, `P2`) `YYYY-MM-DD`.
// ----------------------------------------------------------------------------

export type EsitoRitiro =
  | "rischio_blocco"
  | "richiedi"
  | "da_ricontrollare"
  | "consigliato"
  | "richiesta_inviata"
  | "ritiro_fisso"
  | "nessuna_azione"
  | "nessun_dato_recente"
  | "esclusa"
  | "dismessa";

export type CodiceEtichettaRitiro =
  | "dati_vecchi"
  | "storico_breve"
  | "fisso_proposto"
  | "fisso_oggi"
  | "sabato"
  | "non_riconciliato"
  | "ritiro_non_effettuato"
  | "richiesta_gia_inviata"
  | "gdo_mancante"
  | "non_collegata"
  | "anagrafica_aznext_mancante"
  | "limite_mancante"
  | "valore_assicurato_diverso"
  | "piu_clienti_dati_diversi"
  | "dismissione_programmata";

export interface EtichettaRitiro {
  codice: CodiceEtichettaRitiro;
  testo: string;
}

export interface ScadenzeRitiro {
  D: string;
  P1: string;
  P2: string;
  E1: string;
  E2: string;
  /** La richiesta va fatta prima di quest'ora. */
  scadenza: string;
}

/** Pezzi ed euro in un momento, con la percentuale sul limite che scatta per primo. */
export interface PuntoRitiro {
  pezzi: number;
  euro: number;
  percentuale: number | null;
  limite: "pezzi" | "valore" | null;
}

export interface RigaSituazioneRitiri extends ScadenzeRitiro {
  id_macchina: string;
  nome_punto: string;
  codice_gdo: string | null;
  localita: string | null;
  provincia: string | null;
  clienti: string[];
  trasportatori: { id: string; nome: string }[];
  esito: EsitoRitiro;
  esito_testo: string;
  etichette: EtichettaRitiro[];
  calendario_diverso: boolean;
  trasporto_id_calendario: string | null;
  giacenza: PuntoRitiro;
  previsto_p2: PuntoRitiro | null;
  p1: number | null;
  p2: number | null;
  c2: number | null;
  /** Una richiesta aperta che l'import ha visto non eseguita (v1.14): badge sulla riga. */
  ritiro_non_effettuato: boolean;
}

export interface SituazioneRitiri {
  adesso: string;
  calendario_base: ScadenzeRitiro;
  dati_aggiornati_al: string | null;
  /** Calcolato dal backend: ultima ricezione più vecchia di 24 ore (§ 1.4). */
  dati_non_aggiornati: boolean;
  /** Nell'ordine di gravità del § 7. */
  esiti: { esito: EsitoRitiro; nome: string; conteggio: number }[];
  /** Casseforti con una richiesta segnalata "Ritiro non effettuato" (v1.14). */
  ritiri_non_effettuati: number;
  /**
   * Indice stagionale per categoria, mesi da gennaio (indice 0) a dicembre.
   * Null nei mesi senza versamenti della categoria: lì il fattore è neutro.
   */
  stagionalita: Record<string, (number | null)[]>;
  /** Eventi attivi nel calcolo: con almeno uno gli indici di `stagionalita` sono al netto degli eventi. */
  eventi_attivi: number;
  /** Già ordinate per gravità, poi per p2 decrescente. */
  righe: RigaSituazioneRitiri[];
}

export interface PrevisioneRitiro {
  medio: PuntoRitiro;
  prudente: PuntoRitiro;
}

export interface DettaglioCassaforteRitiri extends RigaSituazioneRitiri {
  adesso: string;
  motivazioni: string[];
  dati: {
    categoria_attivita: string;
    /**
     * `senza_dati`: il mese di E1 o E2 non ha versamenti della categoria, oppure
     * non ne ha nessuna delle ultime 12 settimane: il fattore vale 1.
     */
    fattore_stagionale: {
      e1: number;
      e2: number;
      senza_dati: { e1: boolean; e2: boolean; ultime_12_settimane: boolean };
    } | null;
    versato_medio_giornaliero: number | null;
    intervallo_economico: number | null;
    giorni_al_p1: number | null;
    finestre: number | null;
    vettore: string | null;
    /** `euro` è il 90% di `valore_assicurato` (il massimale pieno, null quando lo è `euro`). */
    limiti: { pezzi: number | null; euro: number | null; valore_assicurato: number | null };
    c1: number | null;
    ultimo_prelievo: string | null;
    ultimo_versamento: string | null;
    ultimo_versamento_ricevuto: string | null;
    /** 1 = lunedì … 7 = domenica. */
    ritiro_fisso_proposto: number | null;
  };
  previsioni: { E1: PrevisioneRitiro; E2: PrevisioneRitiro } | null;
  stagionalita: { categoria: string; indici: (number | null)[] };
  /** Ultimi 21 giorni, dal più vecchio a oggi. */
  curva: { giorno: string; percentuale: number | null }[];
  /** Dalla più recente. */
  richieste: RichiestaRitiro[];
  impostazioni: ImpostazioniCassaforte;
  bozze: PrerequisitiBozze;
}

// ----------------------------------------------------------------------------
// Richieste, bozze email e impostazioni (§§ 5, 6, 7.5, v1.14). Il backend
// decide che cosa si può fare (`bozze_utilizzabili`, motivi, transizioni): la
// pagina lo mostra e non ricalcola le regole.
// ----------------------------------------------------------------------------

export type StatoRichiestaRitiro = "bozza" | "approvata" | "inviata" | "confermata" | "eseguita" | "annullata";
export type TipoRichiestaRitiro = "suggerito" | "extra" | "sollecito";
export type EsecuzioneRitiro = "anticipato" | "nei_tempi" | "in_ritardo";

export interface Passaggio {
  /** Istante ISO dell'app (UTC). */
  il: string;
  da: string | null;
}

export interface BozzaEmail {
  /** Mittente delle bozze: la casella dell'ufficio ritiri (dal template del backend). */
  da: { nome: string; indirizzo: string };
  a: string[];
  cc: string[];
  oggetto: string | null;
  /** Testo semplice: ripiego di "Copia testo". */
  corpo: string | null;
  /** HTML con grassetti e firma: quello che si copia e che apre Outlook. */
  html: string | null;
  /** Solo vigilanza: il corpo senza la firma, quello che si riscrive in bozza (§ 8 bis). */
  testo?: string | null;
}

/** Un tentativo di invio dall'app dell'email alla vigilanza (§ 8 bis). */
export interface InvioEmailRitiro {
  id: string;
  /** `in_invio`: risposta di Graph non arrivata, o esito incerto; l'esito va verificato in Posta inviata. */
  esito: "in_invio" | "inviata" | "errore";
  /** Con `errore`: perché non è partita. Con `in_invio`: l'esito incerto da verificare. */
  errore: string | null;
  a: string[];
  cc: string[];
  oggetto: string;
  reinvio: boolean;
  iniziato_il: string;
  /** Quando Graph l'ha accettato; null se chiuso a mano. */
  inviato_il: string | null;
  da: string | null;
  chiuso_a_mano: Passaggio | null;
  /** Esito incerto, o in invio da più di 2 minuti: si chiude a mano (solo admin). */
  da_verificare: boolean;
}

/** Il tasto Invia: che cosa si può fare adesso, dalla regola del backend. */
export interface StatoInvioRitiro {
  /** Impostazione RITIRI_INVIO_EMAIL_ABILITATO. */
  abilitato: boolean;
  /** Il tasto è attivo (per un reinvio serve comunque la seconda conferma). */
  inviabile: boolean;
  /** Perché non è attivo, dal backend. */
  motivo: string | null;
  /** Inviata dall'app, segnata come inviata o confermata: un nuovo invio è un reinvio. */
  gia_inviata: boolean;
  ultima_inviata: Passaggio | null;
  in_invio: boolean;
}

export interface RichiestaRitiro {
  id: string;
  id_macchina: string;
  tipo: TipoRichiestaRitiro;
  stato: StatoRichiestaRitiro;
  /** `YYYY-MM-DD`. */
  data_ritiro_richiesta: string;
  motivo_extra: string | null;
  richiesta_origine_id: string | null;
  relazione: { id: string; cliente: string } | null;
  creata: Passaggio;
  approvata: Passaggio | null;
  inviata: Passaggio | null;
  confermata: Passaggio | null;
  annullata: (Passaggio & { motivo: string | null }) | null;
  esecuzione: EsecuzioneRitiro | null;
  giorni_ritardo: number | null;
  /** Ora italiana del prelievo che l'ha resa eseguita. */
  prelievo_il: string | null;
  non_effettuata: boolean;
  /** "Non verificabile: la cassaforte non trasmette dal gg/mm" (v1.15), o null. */
  non_verificabile: string | null;
  vigilanza: BozzaEmail;
  /** Null per i solleciti (solo vigilanza) e quando la bozza al cliente non è stata generata. */
  cliente: BozzaEmail | null;
  /** Perché la bozza al cliente manca ("Manca email cliente"…), o null (v1.16). */
  cliente_bloccata: MotivoBozza[] | null;
  /** La bozza al cliente mancante si può generare adesso (email completata, ritiro non eseguito né annullato). */
  puo_generare_cliente: boolean;
  avvisi: string[];
  /** Testo copiabile e `.eml`: solo dopo l'approvazione, se obbligatoria. */
  bozze_utilizzabili: boolean;
  /** Stati raggiungibili con la PATCH (la regola è del backend). "approvata" richiede anche ritiri:approva. */
  azioni: ("approvata" | "inviata" | "confermata" | "annullata")[];
  puo_sollecitare: boolean;
  /** Solleciti non chiusi di questa richiesta: annullandola si annullano con lei. 0 per un sollecito. */
  solleciti_aperti: number;
  /** Cambia a ogni scrittura: l'approvazione la rimanda, per approvare il testo che si ha davanti. */
  versione: string;
  /** La bozza alla vigilanza si riscrive (solo in bozza). */
  bozza_modificabile: boolean;
  invio: StatoInvioRitiro;
  /** Tentativi di invio dall'app, dal più recente. */
  invii: InvioEmailRitiro[];
}

/** Un dato che manca, con il posto dove completarlo (la pagina ci mette il link). */
export interface MotivoBozza {
  codice: string;
  /** Es. "Manca codice GDO", "Manca email cliente". */
  testo: string;
  dove: "anagrafica_device" | "impostazioni_cassaforte" | "impostazioni_sistema" | null;
}

export interface PrerequisitiBozze {
  /** Il tipo che avrebbe una richiesta creata adesso: "extra" se l'esito non suggerisce un ritiro. */
  tipo_nuova_richiesta: "suggerito" | "extra";
  /** Il device della cassaforte, per il link all'anagrafica. */
  device_id: string | null;
  /** Motivi della cassaforte: bloccano tutte le bozze. */
  motivi: MotivoBozza[];
  scelta_necessaria: boolean;
  /**
   * Per relazione, i motivi bozza per bozza (v1.16): quelli della vigilanza
   * bloccano la richiesta, quelli del cliente solo l'avviso al cliente.
   */
  relazioni: {
    id: string;
    cliente: string;
    sede: string | null;
    codice_gdo: string | null;
    servizio: string | null;
    motivi_vigilanza: MotivoBozza[];
    motivi_cliente: MotivoBozza[];
  }[];
  approvazione_obbligatoria: boolean;
}

export interface ImpostazioniCassaforte {
  categoria_attivita: string | null;
  categoria_origine: "script" | "manuale" | null;
  /** 1 = lunedì … 5 = venerdì. */
  giorno_ritiro_fisso: number | null;
  ritiro_fisso_confermato: Passaggio | null;
  orario_esercizio: string | null;
  email_avviso_cliente: string[];
  email_punto_vendita_aznext: string | null;
  escludi_dal_calcolo: boolean;
  motivo_esclusione: string | null;
  note: string | null;
  modificate: Passaggio | null;
}

export interface ModificaImpostazioni {
  /**
   * La versione letta all'apertura del form (`modificate.il`, o null se mai
   * salvate): se nel frattempo un altro utente le ha modificate, il backend
   * risponde 409 e non scrive.
   */
  versione: string | null;
  categoria_attivita?: string | null;
  giorno_ritiro_fisso?: number | null;
  ritiro_fisso_confermato?: boolean;
  orario_esercizio?: string | null;
  email_avviso_cliente?: string;
  escludi_dal_calcolo?: boolean;
  motivo_esclusione?: string | null;
  note?: string | null;
}

export async function aggiornaImpostazioni(idMacchina: string, modifica: ModificaImpostazioni): Promise<ImpostazioniCassaforte> {
  const { data } = await apiClient.patch<ImpostazioniCassaforte>(
    `/ritiri/casseforti/${encodeURIComponent(idMacchina)}/impostazioni`,
    modifica
  );
  return data;
}

export async function creaRichiesta(corpo: { id_macchina: string; device_cliente_id?: string; motivo_extra?: string }): Promise<RichiestaRitiro> {
  const { data } = await apiClient.post<RichiestaRitiro>("/ritiri/richieste", corpo);
  return data;
}

export async function cambiaStatoRichiesta(
  id: string,
  corpo: { stato: "approvata" | "inviata" | "confermata" | "annullata"; motivo_annullamento?: string; versione?: string }
): Promise<RichiestaRitiro> {
  const { data } = await apiClient.patch<RichiestaRitiro>(`/ritiri/richieste/${encodeURIComponent(id)}`, corpo);
  return data;
}

/**
 * La bozza alla vigilanza riscritta a mano, prima dell'approvazione (§ 8 bis).
 * `testo` è il corpo senza firma; `versione` quella della richiesta aperta
 * nell'editor: se un altro l'ha salvata nel frattempo, 409.
 */
export async function modificaBozzaVigilanza(
  id: string,
  corpo: { a: string[]; cc: string[]; oggetto: string; testo: string; versione: string }
): Promise<RichiestaRitiro> {
  const { data } = await apiClient.patch<RichiestaRitiro>(`/ritiri/richieste/${encodeURIComponent(id)}/bozza-vigilanza`, corpo);
  return data;
}

/**
 * Il tasto Invia (§ 8 bis): parte solo con `conferma: true`, cioè dal clic su
 * "Conferma invio". Se Graph rifiuta il backend risponde 502 con l'errore: la
 * promessa si rifiuta e la richiesta resta approvata.
 */
export async function inviaEmailRitiro(id: string, corpo: { conferma: true; reinvio?: true }): Promise<{ esito: "inviata"; richiesta: RichiestaRitiro }> {
  const { data } = await apiClient.post<{ esito: "inviata"; richiesta: RichiestaRitiro }>(`/ritiri/richieste/${encodeURIComponent(id)}/invia`, corpo);
  return data;
}

/** Chiude a mano un tentativo rimasto in invio, dopo la verifica in Posta inviata (solo admin). */
export async function chiudiInvioRitiro(invioId: string, corpo: { esito: "inviata" | "non_inviata"; nota?: string }): Promise<RichiestaRitiro> {
  const { data } = await apiClient.post<RichiestaRitiro>(`/ritiri/invii/${encodeURIComponent(invioId)}/chiudi`, corpo);
  return data;
}

/** Genera la bozza al cliente mancante, finché la richiesta è in bozza (v1.16). */
export async function generaBozzaCliente(id: string): Promise<RichiestaRitiro> {
  const { data } = await apiClient.post<RichiestaRitiro>(`/ritiri/richieste/${encodeURIComponent(id)}/bozza-cliente`);
  return data;
}

export async function creaSollecito(id: string): Promise<RichiestaRitiro> {
  const { data } = await apiClient.post<RichiestaRitiro>(`/ritiri/richieste/${encodeURIComponent(id)}/sollecito`);
  return data;
}

/** Il file `.eml` (X-Unsent: 1) come Blob, con il nome proposto dal backend. */
export async function scaricaEml(id: string, tipo: "vigilanza" | "cliente"): Promise<{ blob: Blob; nome: string }> {
  try {
    const risposta = await apiClient.get<Blob>(`/ritiri/richieste/${encodeURIComponent(id)}/eml`, { params: { tipo }, responseType: "blob" });
    const intestazione = String(risposta.headers["content-disposition"] ?? "");
    const nome = /filename="([^"]+)"/.exec(intestazione)?.[1] ?? `${tipo}.eml`;
    return { blob: risposta.data, nome };
  } catch (err) {
    // Con `responseType: "blob"` anche l'errore arriva come Blob: se ne legge il
    // messaggio JSON, così a schermo va il testo del backend.
    const dati = (err as { response?: { data?: unknown } }).response?.data;
    if (dati instanceof Blob) {
      const testo = await dati.text().catch(() => "");
      let messaggio: string | undefined;
      try {
        messaggio = (JSON.parse(testo) as { error?: string }).error;
      } catch {
        messaggio = undefined;
      }
      if (messaggio) throw new Error(messaggio);
    }
    throw err;
  }
}

export interface RigaElencoRichieste {
  id: string;
  id_macchina: string;
  nome_punto: string;
  cliente: string | null;
  tipo: TipoRichiestaRitiro;
  stato: StatoRichiestaRitiro;
  data_ritiro_richiesta: string;
  created_at: string;
  esecuzione: EsecuzioneRitiro | null;
  giorni_ritardo: number | null;
  non_effettuata: boolean;
  non_verificabile: string | null;
  richiesta_origine_id: string | null;
}

/** Una pagina dell'elenco (v1.17): `totale` conta tutte le richieste con quei filtri. */
export interface PaginaRichieste {
  richieste: RigaElencoRichieste[];
  totale: number;
  page: number;
  limit: number;
}

export async function elencoRichieste(
  filtri: { stato?: string; tipo?: string; id_macchina?: string; da?: string; a?: string },
  pagina: { page: number; limit?: number }
): Promise<PaginaRichieste> {
  const params = { ...Object.fromEntries(Object.entries(filtri).filter(([, v]) => v)), ...pagina };
  const { data } = await apiClient.get<PaginaRichieste>("/ritiri/richieste", { params });
  return data;
}

export interface ConteggiStorico {
  richieste: number;
  anticipate: number;
  nei_tempi: number;
  in_ritardo: number;
  /** Tutte le segnalate "Ritiro non effettuato", anche se poi eseguite (v1.16). */
  non_effettuate: number;
  /** Di cui poi eseguite in ritardo (v1.16). */
  non_effettuate_poi_eseguite: number;
  in_attesa: number;
  solleciti: number;
}

export interface StoricoRichieste {
  per_cassaforte: (ConteggiStorico & { id_macchina: string; nome_punto: string })[];
  per_trasportatore: (ConteggiStorico & { trasportatore: string })[];
}

export async function storicoRichieste(): Promise<StoricoRichieste> {
  const { data } = await apiClient.get<StoricoRichieste>("/ritiri/storico");
  return data;
}

export interface TrasportatoreRitiri {
  id: string;
  nome: string;
  /** Casseforti AZNext servite oggi (relazione attiva con questo trasportatore). */
  casseforti_aznext: number;
}

export async function trasportatoriRitiri(): Promise<TrasportatoreRitiri[]> {
  const { data } = await apiClient.get<TrasportatoreRitiri[]>("/ritiri/trasportatori");
  return data;
}

export async function creaGiornoSenzaRitiro(corpo: { data: string; trasporto_valori_id?: string; descrizione?: string }): Promise<void> {
  await apiClient.post("/ritiri/giorni-senza-ritiro", corpo);
}

export async function eliminaGiornoSenzaRitiro(id: string): Promise<void> {
  await apiClient.delete(`/ritiri/giorni-senza-ritiro/${encodeURIComponent(id)}`);
}

export interface GiornoSenzaRitiro {
  id: string;
  data: string;
  descrizione: string | null;
  created_at: string;
  /** Null = vale per tutte le casseforti. */
  trasporto_valori: { id: string; nome: string } | null;
  creator: { id: string; full_name: string | null } | null;
}

export async function situazioneRitiri(): Promise<SituazioneRitiri> {
  const { data } = await apiClient.get<SituazioneRitiri>("/ritiri/situazione");
  return data;
}

export async function dettaglioCassaforteRitiri(idMacchina: string): Promise<DettaglioCassaforteRitiri> {
  const { data } = await apiClient.get<DettaglioCassaforteRitiri>(`/ritiri/casseforti/${encodeURIComponent(idMacchina)}`);
  return data;
}

export async function giorniSenzaRitiro(filtro: { da?: string; a?: string } = {}): Promise<GiornoSenzaRitiro[]> {
  const { data } = await apiClient.get<GiornoSenzaRitiro[]>("/ritiri/giorni-senza-ritiro", { params: filtro });
  return data;
}

// ----------------------------------------------------------------------------
// Calendario eventi (§ 4 bis)
// ----------------------------------------------------------------------------

/**
 * `fissa`: giorno e mese di inizio e fine. `pasqua`: giorni dalla domenica di
 * Pasqua. `settimana_mese`: giorni dall'N-esimo giorno della settimana di un
 * mese (Black Friday = 4° giovedì di novembre, da +1 a +1).
 */
export type TipoDataEvento = "fissa" | "pasqua" | "settimana_mese";

export interface EffettoEvento {
  categoria_attivita: string;
  /** Null = tutta Italia. */
  regione: string | null;
  /** Da 0,10 a 5,00: 1 = nessun effetto. */
  moltiplicatore: number;
  /** Giorni di cui l'effetto si sposta dopo le date dell'evento (0–7). */
  ritardo_giorni: number;
}

export interface DateEvento {
  tipo_data: TipoDataEvento;
  giorno_inizio: number | null;
  mese_inizio: number | null;
  giorno_fine: number | null;
  mese_fine: number | null;
  scostamento_inizio: number | null;
  scostamento_fine: number | null;
  /** Solo `settimana_mese`: il mese del giorno di riferimento (1–12). */
  mese_riferimento: number | null;
  /** Solo `settimana_mese`: 1–4, o −1 = l'ultimo del mese. */
  ordinale: number | null;
  /** Solo `settimana_mese`: 1 = lunedì … 7 = domenica. */
  giorno_settimana: number | null;
}

export interface EventoRitiri extends DateEvento {
  id: string;
  nome: string;
  attivo: boolean;
  note: string | null;
  created_at: string;
  updated_at: string;
  creator: { id: string; full_name: string | null } | null;
  updater: { id: string; full_name: string | null } | null;
  effetti: (EffettoEvento & { id: string })[];
  /** Le date nell'anno in corso e nel successivo (`YYYY-MM-DD`, estremi inclusi). */
  date: { anno: number; inizio: string; fine: string }[];
}

export interface CalendarioEventi {
  eventi: EventoRitiri[];
  /** Gli elenchi fissi del backend, per i menu. */
  categorie: string[];
  regioni: string[];
}

/** Corpo di creazione e modifica: `attivo` si cambia solo con l'interruttore. */
export interface CorpoEvento extends DateEvento {
  nome: string;
  note: string | null;
  effetti: EffettoEvento[];
}

export async function calendarioEventi(): Promise<CalendarioEventi> {
  const { data } = await apiClient.get<CalendarioEventi>("/ritiri/eventi");
  return data;
}

export async function creaEvento(corpo: CorpoEvento): Promise<EventoRitiri> {
  const { data } = await apiClient.post<EventoRitiri>("/ritiri/eventi", corpo);
  return data;
}

export async function aggiornaEvento(id: string, corpo: CorpoEvento): Promise<EventoRitiri> {
  const { data } = await apiClient.put<EventoRitiri>(`/ritiri/eventi/${encodeURIComponent(id)}`, corpo);
  return data;
}

export async function impostaAttivoEvento(id: string, attivo: boolean): Promise<EventoRitiri> {
  const { data } = await apiClient.patch<EventoRitiri>(`/ritiri/eventi/${encodeURIComponent(id)}/attivo`, { attivo });
  return data;
}

export async function eliminaEvento(id: string): Promise<void> {
  await apiClient.delete(`/ritiri/eventi/${encodeURIComponent(id)}`);
}
